import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdminService } from '../../services/adminService';

const SOURCE_COLOR = {
  heuristic: 'success',
  llm: 'warning',
  reviewed: 'secondary',
};

const FAMILIES = [
  'homophone',
  'agreement',
  'conjugation',
  'lexical',
  'segmentation',
  'punctuation',
  'vocabulary',
  'hors_taxonomie',
  'illegible',
];

function fmtDate(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('fr-FR');
  } catch {
    return String(v);
  }
}

function notionCell(id, label) {
  if (!id) return '—';
  return (
    <Box>
      <Typography variant="body2">{label || id}</Typography>
      {label && label !== id ? (
        <Typography variant="caption" color="text.secondary">{id}</Typography>
      ) : null}
    </Box>
  );
}

export default function ErrorClassificationCachePanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [source, setSource] = useState('');
  const [family, setFamily] = useState('');
  const [unclassified, setUnclassified] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, source, family, unclassified]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AdminService.listErrorClassificationCache({
        search: debouncedSearch || undefined,
        source: source || undefined,
        errorFamily: family || undefined,
        unclassified: unclassified || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.total) || 0);
      setStats(data?.stats || null);
    } catch (e) {
      setError(e?.message || 'Impossible de lire le cache');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, source, family, unclassified, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 920 }}>
        Table <code>error_classifications</code> : un couple (forme attendue, forme
        produite) → notion observée. Pas d&apos;user_id. Une ligne{' '}
        <strong>heuristic</strong> ou <strong>reviewed</strong> n&apos;est jamais
        écrasée par le LLM (D-7) — d&apos;où le TRUNCATE (migr. 121) quand le
        mapping change : sinon les anciennes lignes restent verrouillées.
      </Typography>

      {stats && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${stats.pairCount} couples`} />
          <Chip label={`${stats.hitSum} hits`} variant="outlined" />
          {Object.entries(stats.bySource || {}).map(([k, n]) => (
            <Chip key={k} label={`${k} ${n}`} color={SOURCE_COLOR[k] || 'default'} variant="outlined" />
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField
          size="small"
          label="Recherche"
          placeholder="maisons, accord_adjectif…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Source</InputLabel>
          <Select
            label="Source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            <MenuItem value="">Toutes</MenuItem>
            <MenuItem value="heuristic">heuristic</MenuItem>
            <MenuItem value="llm">llm</MenuItem>
            <MenuItem value="reviewed">reviewed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Famille</InputLabel>
          <Select
            label="Famille"
            value={family}
            onChange={(e) => setFamily(e.target.value)}
          >
            <MenuItem value="">Toutes</MenuItem>
            {FAMILIES.map((f) => (
              <MenuItem key={f} value={f}>{f}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={unclassified}
              onChange={(e) => setUnclassified(e.target.checked)}
            />
          }
          label="Sans notion"
        />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              Aucune ligne — cache vide, ou aucun couple ne correspond aux filtres.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 640 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Attendu</TableCell>
                  <TableCell>Produit</TableCell>
                  <TableCell>Notion cache</TableCell>
                  <TableCell>Famille</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Hits</TableCell>
                  <TableCell>Heuristique actuelle</TableCell>
                  <TableCell>Vu</TableCell>
                  <TableCell>Explication</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const heurId = row.heuristic?.observedNotionId || null;
                  const mismatch = Boolean(
                    row.heuristic
                    && heurId !== (row.observedNotionId || null)
                  );
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      sx={mismatch ? { bgcolor: 'action.selected' } : undefined}
                    >
                      <TableCell><code>{row.expectedNorm}</code></TableCell>
                      <TableCell><code>{row.producedNorm}</code></TableCell>
                      <TableCell>
                        {notionCell(row.observedNotionId, row.observedNotionLabel)}
                      </TableCell>
                      <TableCell>{row.errorFamily || '—'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            size="small"
                            label={row.source}
                            color={SOURCE_COLOR[row.source] || 'default'}
                          />
                          {row.locked && (
                            <Tooltip title="Le LLM ne peut pas écraser cette ligne (D-7).">
                              <Chip size="small" label="verrouillé" variant="outlined" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{row.hitCount}</TableCell>
                      <TableCell>
                        {row.heuristic ? (
                          <Box>
                            <Typography variant="body2">
                              {row.heuristic.observedNotionId || '—'}
                              {row.heuristic.trapId ? ` (${row.heuristic.trapId})` : ''}
                            </Typography>
                            {mismatch && (
                              <Typography variant="caption" color="warning.main">
                                différent du cache
                              </Typography>
                            )}
                            {row.heuristic.caveat && (
                              <Tooltip title={row.heuristic.caveat}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  limite connue
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell>{fmtDate(row.lastSeenAt)}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        {row.explanation ? (
                          <Tooltip title={row.explanation}>
                            <Typography variant="caption" noWrap>
                              {row.explanation}
                            </Typography>
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelRowsPerPage="Lignes"
          />
        </>
      )}
    </Stack>
  );
}
